import { useState, useEffect } from 'react';
import { fetchPreparedResponses } from '../../services/commentService';
import { Loader2, Search, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface PreparedResponsesProps {
  onSelectResponse: (response: string) => void;
}

interface PreparedResponse {
  id: string;
  content: string;
  category: string;
}

const PreparedResponses = ({ onSelectResponse }: PreparedResponsesProps) => {
  const [responses, setResponses] = useState<PreparedResponse[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<PreparedResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newResponseContent, setNewResponseContent] = useState('');
  const [newResponseCategory, setNewResponseCategory] = useState('Général');

  useEffect(() => {
    loadResponses();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = responses.filter(
        (response) =>
          response.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
          response.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredResponses(filtered);
    } else {
      setFilteredResponses(responses);
    }
  }, [searchQuery, responses]);

  const loadResponses = async () => {
    setIsLoading(true);
    try {
      const fetchedResponses = await fetchPreparedResponses();
      setResponses(fetchedResponses);
      setFilteredResponses(fetchedResponses);
    } catch (error) {
      console.error('Erreur lors de la récupération des réponses préparées:', error);
      // Fallback to mock data if database table doesn't exist yet
      const mockResponses = [
        { id: '1', content: 'Merci pour votre commentaire ! Nous apprécions votre feedback.', category: 'Remerciement' },
        { id: '2', content: 'Nous sommes désolés pour cette expérience. Pouvez-vous nous fournir plus de détails ?', category: 'Service client' },
        { id: '3', content: 'Nous avons bien pris note de votre demande et allons la traiter rapidement.', category: 'Service client' },
        { id: '4', content: 'Heureux que notre produit vous plaise ! N\'hésitez pas à partager votre expérience.', category: 'Positif' }
      ];
      setResponses(mockResponses);
      setFilteredResponses(mockResponses);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectResponse = (response: string) => {
    onSelectResponse(response);
  };

  const handleAddResponse = () => {
    if (!newResponseContent.trim()) return;
    
    const newResponse = {
      id: Date.now().toString(),
      content: newResponseContent,
      category: newResponseCategory,
    };
    
    setResponses([...responses, newResponse]);
    setNewResponseContent('');
    setShowAddForm(false);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-custom-primary dark:text-custom-secondaryLight mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Chargement des réponses préparées...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Réponses préparées</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
          className="text-gray-600 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800/80"
        >
          <Plus className="h-4 w-4 mr-1" />
          Ajouter
        </Button>
      </div>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <Input
          type="text"
          placeholder="Rechercher une réponse..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white dark:bg-gray-800/90 border-gray-300 dark:border-gray-700 transition-colors"
        />
      </div>

      {showAddForm && (
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/40">
          <div className="space-y-3">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Catégorie
              </label>
              <Input
                id="category"
                type="text"
                value={newResponseCategory}
                onChange={(e) => setNewResponseCategory(e.target.value)}
                placeholder="Catégorie (ex: Service client)"
                className="bg-white dark:bg-gray-800/90 border-gray-300 dark:border-gray-700 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contenu
              </label>
              <textarea
                id="content"
                value={newResponseContent}
                onChange={(e) => setNewResponseContent(e.target.value)}
                placeholder="Texte de la réponse..."
                rows={3}
                className="w-full p-2 rounded-md bg-white dark:bg-gray-800/90 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-200 transition-colors"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(false)}
                className="text-gray-600 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800/80"
              >
                Annuler
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleAddResponse}
                disabled={!newResponseContent.trim()}
                className="bg-custom-primary hover:bg-custom-dark text-white dark:bg-custom-secondaryLight dark:text-gray-900 dark:hover:bg-custom-secondaryLight/90"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {filteredResponses.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">Aucune réponse trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredResponses.map((response) => (
            <div
              key={response.id}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full">
                  {response.category}
                </span>
              </div>
              <p className="text-gray-800 dark:text-gray-200 mb-3">{response.content}</p>
              <div className="flex justify-end">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleSelectResponse(response.content)}
                  className="bg-custom-primary hover:bg-custom-dark text-white dark:bg-custom-secondaryLight dark:text-gray-900 dark:hover:bg-custom-secondaryLight/90"
                >
                  Utiliser
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PreparedResponses;
